"use client";

import {
  HighlightedText,
  type TextMatch,
} from "@/components/tool-kit/highlighted-text";
import {
  CopyButton,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  HINT,
  TABLE,
  TD,
  TEXTAREA,
  TH,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { DNA_TO_PROTEIN_TRANSLATOR_STRINGS } from "@/components/tools/dna-to-protein-translator";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { toFasta } from "@/lib/tool-kit/biology-genetics/fasta";
import {
  toThreeLetter,
  translateCodon,
  translateFrame,
} from "@/lib/tool-kit/biology-genetics/genetic-code";
import { reverseComplement } from "@/lib/tool-kit/biology-genetics/iupac";
import type { Orf } from "@/lib/tool-kit/biology-genetics/orf";

export type TranslatorStrings = typeof DNA_TO_PROTEIN_TRANSLATOR_STRINGS;
export type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;
export type AminoFormat = "one" | "three";

/** Bases shown with highlighting; the protein and tables always cover the whole sequence. */
const DISPLAY_LIMIT = 6000;
const CODON_TABLE_LIMIT = 300;
const PROTEIN_PREVIEW = 30;
const ORF_ROWS = 200;
const TONES = {
  start: "emerald",
  stop: "rose",
  forward: "amber",
  reverse: "sky",
} as const;

export function frameLabel(frame: number): string {
  return frame > 0 ? `+${frame}` : `−${Math.abs(frame)}`;
}

/** The strand a frame reads, and its 0-based offset on that strand. */
export function strandFor(
  sequence: string,
  frame: number
): { strand: string; offset: number } {
  return {
    strand: frame > 0 ? sequence : reverseComplement(sequence),
    offset: Math.abs(frame) - 1,
  };
}

export function formatProtein(protein: string, format: AminoFormat): string {
  return format === "three" ? toThreeLetter(protein) : protein;
}

function toRna(sequence: string): string {
  return sequence.replaceAll("T", "U");
}

/** The frame's bases split into codons, with the start and stop codons marked. */
function codonLayout(
  strand: string,
  offset: number
): { text: string; matches: TextMatch[]; shown: number } {
  const limit = Math.min(strand.length, DISPLAY_LIMIT + offset);
  let text = offset > 0 ? `${strand.slice(0, offset)} ` : "";
  const matches: TextMatch[] = [];
  let i = offset;
  for (; i + 3 <= limit; i += 3) {
    const codon = strand.slice(i, i + 3);
    const aa = translateCodon(codon);
    if (codon === "ATG" || aa === "*") {
      matches.push({
        start: text.length,
        end: text.length + 3,
        category: aa === "*" ? "stop" : "start",
      });
    }
    text += `${codon} `;
  }
  if (limit === strand.length && i < strand.length) {
    text += strand.slice(i);
  }
  return { text: text.trimEnd(), matches, shown: Math.min(i, strand.length) };
}

function CopyRow({
  items,
  copied,
}: {
  items: { label: string; text: string }[];
  copied: string;
}) {
  return (
    <div className="flex flex-wrap gap-3">
      {items.map((item) => (
        <CopyButton
          copiedLabel={copied}
          key={item.label}
          label={item.label}
          text={item.text}
        />
      ))}
    </div>
  );
}

export function SingleFrameView({
  name,
  sequence,
  frame,
  toStop,
  fromStart,
  format,
  strings,
  fmt,
  locale,
}: {
  name: string;
  sequence: string;
  frame: number;
  toStop: boolean;
  fromStart: boolean;
  format: AminoFormat;
  strings: TranslatorStrings;
  fmt: Fmt;
  locale: string;
}) {
  const { strand, offset } = strandFor(sequence, frame);
  const translation = translateFrame(strand, offset, {
    toStop,
    fromFirstStart: fromStart,
  });
  const protein = translation.protein;
  const stops = [...protein].filter((aa) => aa === "*").length;
  const layout = codonLayout(strand, offset);
  const proteinText = formatProtein(protein, format);
  const read = strand.slice(translation.startIndex, translation.endIndex);
  const codons = Math.floor(read.length / 3);
  const rows = Array.from(
    { length: Math.min(codons, CODON_TABLE_LIMIT) },
    (_, index) => {
      const codon = read.slice(index * 3, index * 3 + 3);
      return { codon, aa: translateCodon(codon) };
    }
  );
  const fastaName = `${name} frame ${frameLabel(frame)}`;
  return (
    <div className="space-y-4">
      <StatGrid>
        <StatCard
          label={strings.lengthNt}
          value={`${fmt(sequence.length)} ${strings.unitNt}`}
        />
        <StatCard
          emphasis
          label={strings.proteinLength}
          value={`${fmt(protein.length - stops)} ${strings.unitAa}`}
        />
        <StatCard label={strings.stopCodons} value={fmt(stops)} />
      </StatGrid>
      {translation.leftover > 0 ? (
        <Notice>
          {tPlural(strings.leftover, locale, translation.leftover, {
            count: fmt(translation.leftover),
          })}
        </Notice>
      ) : null}
      <div className="space-y-2">
        <h4 className="font-medium text-foreground text-sm">
          {strings.proteinHeading}
        </h4>
        <textarea
          aria-label={strings.proteinHeading}
          className={TEXTAREA}
          readOnly
          rows={5}
          value={proteinText}
        />
        <CopyRow
          copied={strings.copied}
          items={[
            { label: strings.copyProtein, text: proteinText },
            { label: strings.copyFasta, text: toFasta(fastaName, protein) },
          ]}
        />
      </div>
      <div className="space-y-2">
        <h4 className="font-medium text-foreground text-sm">
          {strings.mrnaHeading}
        </h4>
        <textarea
          aria-label={strings.mrnaHeading}
          className={TEXTAREA}
          readOnly
          rows={4}
          value={toRna(strand)}
        />
        <CopyRow
          copied={strings.copied}
          items={[{ label: strings.copyMrna, text: toRna(strand) }]}
        />
      </div>
      <div className="space-y-2">
        <p className={HINT}>{strings.highlightLegend}</p>
        <div className="font-mono">
          <HighlightedText
            label={strings.highlightLabel}
            matches={layout.matches}
            text={layout.text}
            tones={TONES}
          />
        </div>
        {strand.length > layout.shown + 2 ? (
          <p className={HINT}>
            {t(strings.truncated, { count: fmt(layout.shown) })}
          </p>
        ) : null}
      </div>
      {rows.length > 0 ? (
        <details className="rounded-lg border border-border bg-card p-3">
          <summary className="cursor-pointer font-medium text-foreground text-sm">
            {strings.codonTable}
          </summary>
          <div className="mt-3 max-h-80 overflow-auto">
            <table className={TABLE}>
              <thead>
                <tr>
                  <th className={TH} scope="col">
                    {strings.numberColumn}
                  </th>
                  <th className={TH} scope="col">
                    {strings.codonColumn}
                  </th>
                  <th className={TH} scope="col">
                    {strings.threeColumn}
                  </th>
                  <th className={TH} scope="col">
                    {strings.oneColumn}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: codon positions are the identity
                  <tr key={index}>
                    <td className={TD}>{fmt(index + 1)}</td>
                    <td className={`${TD} font-mono`}>{toRna(row.codon)}</td>
                    <td className={TD}>{toThreeLetter(row.aa)}</td>
                    <td className={`${TD} font-mono`}>{row.aa}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      ) : null}
    </div>
  );
}

export function SixFrameView({
  name,
  sequence,
  toStop,
  fromStart,
  format,
  strings,
  fmt,
  locale,
}: {
  name: string;
  sequence: string;
  toStop: boolean;
  fromStart: boolean;
  format: AminoFormat;
  strings: TranslatorStrings;
  fmt: Fmt;
  locale: string;
}) {
  const frames = [1, 2, 3, -1, -2, -3].map((frame) => {
    const { strand, offset } = strandFor(sequence, frame);
    const protein = translateFrame(strand, offset, {
      toStop,
      fromFirstStart: fromStart,
    }).protein;
    return {
      frame,
      protein,
      stops: [...protein].filter((aa) => aa === "*").length,
    };
  });
  const fasta = frames
    .map((entry) =>
      toFasta(`${name} frame ${frameLabel(entry.frame)}`, entry.protein)
    )
    .join("\n");
  return (
    <div className="space-y-4">
      {frames.map((entry) => (
        <div className="space-y-2" key={entry.frame}>
          <h4 className="font-medium text-foreground text-sm">
            {t(strings.frameHeading, { frame: frameLabel(entry.frame) })}
            <span className="ml-2 font-normal text-muted-foreground">
              {tPlural(strings.frameStops, locale, entry.stops, {
                count: fmt(entry.stops),
              })}
            </span>
          </h4>
          <textarea
            aria-label={t(strings.frameHeading, {
              frame: frameLabel(entry.frame),
            })}
            className={TEXTAREA}
            readOnly
            rows={3}
            value={formatProtein(entry.protein, format)}
          />
        </div>
      ))}
      <CopyRow
        copied={strings.copied}
        items={[{ label: strings.copyAllFasta, text: fasta }]}
      />
    </div>
  );
}

export function OrfView({
  name,
  sequence,
  orfs,
  minAminoAcids,
  strings,
  fmt,
  locale,
}: {
  name: string;
  sequence: string;
  orfs: readonly Orf[];
  minAminoAcids: number;
  strings: TranslatorStrings;
  fmt: Fmt;
  locale: string;
}) {
  if (orfs.length === 0) {
    return <Notice>{t(strings.noOrfs, { min: fmt(minAminoAcids) })}</Notice>;
  }
  const shown = sequence.slice(0, DISPLAY_LIMIT);
  const matches: TextMatch[] = orfs
    .filter((orf) => orf.end <= DISPLAY_LIMIT)
    .map((orf) => ({
      start: orf.start - 1,
      end: orf.end,
      category: orf.strand === "+" ? "forward" : "reverse",
    }));
  const fasta = orfs
    .map((orf, index) =>
      toFasta(
        `${name} ORF${index + 1} frame ${frameLabel(orf.frame)} ${orf.start}-${orf.end}`,
        orf.protein
      )
    )
    .join("\n");
  return (
    <div className="space-y-4">
      <p aria-live="polite" className="font-medium text-foreground text-sm">
        {tPlural(strings.orfsFound, locale, orfs.length, {
          count: fmt(orfs.length),
        })}
      </p>
      <div className="max-h-96 overflow-auto rounded-lg border border-border bg-card">
        <table className={TABLE}>
          <thead>
            <tr>
              {[
                strings.numberColumn,
                strings.frameColumn,
                strings.startColumn,
                strings.endColumn,
                strings.ntColumn,
                strings.aaColumn,
                strings.proteinColumn,
              ].map((heading) => (
                <th className={TH} key={heading} scope="col">
                  {heading}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {orfs.slice(0, ORF_ROWS).map((orf, index) => (
              <tr key={`${orf.frame}-${orf.start}`}>
                <td className={TD}>{fmt(index + 1)}</td>
                <td className={TD}>{frameLabel(orf.frame)}</td>
                <td className={TD}>{fmt(orf.start)}</td>
                <td className={TD}>{fmt(orf.end)}</td>
                <td className={TD}>{fmt(orf.nucleotides)}</td>
                <td className={TD}>
                  {fmt(orf.aminoAcids)}
                  {orf.complete ? null : ` (${strings.noStop})`}
                </td>
                <td className={`${TD} font-mono`}>
                  {orf.protein.length > PROTEIN_PREVIEW
                    ? `${orf.protein.slice(0, PROTEIN_PREVIEW)}…`
                    : orf.protein}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {orfs.length > ORF_ROWS ? (
        <p className={HINT}>
          {t(strings.orfRowsLimit, { count: fmt(ORF_ROWS) })}
        </p>
      ) : null}
      <CopyRow
        copied={strings.copied}
        items={[{ label: strings.copyOrfFasta, text: fasta }]}
      />
      <div className="space-y-2">
        <h4 className={WIDGET_H3}>{strings.orfMapHeading}</h4>
        <p className={HINT}>{strings.orfLegend}</p>
        <div className="font-mono">
          <HighlightedText
            label={strings.orfMapLabel}
            matches={matches}
            text={shown}
            tones={TONES}
          />
        </div>
        {sequence.length > DISPLAY_LIMIT ? (
          <p className={HINT}>
            {t(strings.truncated, { count: fmt(DISPLAY_LIMIT) })}
          </p>
        ) : null}
      </div>
    </div>
  );
}
